import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import { convertISTToTimezone, formatDateToISO, calculateDuration, } from "./utils/date";
import { useTranslation } from "../../hooks";
import { chargeRow } from "./utils/chargeRow";
const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
export function SuccessStep({ state, config, booked = null, }) {
    const { t } = useTranslation("appointment");
    const selectedSlot = booked?.slot ?? state.selectedSlot;
    const selectedDate = booked?.date ?? state.selectedDate;
    const selectedTimezone = booked?.timezone ?? state.selectedTimezone;
    const consultationMode = booked?.mode ?? state.consultationMode;
    const { selectedDoctor, selectedPatient, newPatientForm, patientTab, countryCode, patientPhone, selectedDoctorDisplay, } = state;
    const slotTimeFormat = config?.slotTimeFormat ?? "start";
    const showDuration = config?.showDuration ?? true;
    const patientName = patientTab === "existing" && selectedPatient
        ? [selectedPatient.firstName, selectedPatient.lastName]
            .filter(Boolean)
            .join(" ")
        : newPatientForm.fullName;
    const timeDisplay = useMemo(() => {
        if (!selectedSlot)
            return null;
        const dateStr = formatDateToISO(selectedDate);
        const startStr = selectedSlot.start.includes("T")
            ? selectedSlot.start.split("T")[1].substring(0, 5)
            : selectedSlot.start.substring(0, 5);
        const displayStart = convertISTToTimezone(startStr, dateStr, selectedTimezone);
        const day = DAYS[selectedDate.getDay()];
        const month = MONTHS[selectedDate.getMonth()];
        const date = selectedDate.getDate();
        const mode = consultationMode === "ONLINE" ? t("online") : t("inPerson");
        if (slotTimeFormat === "range") {
            const endStr = selectedSlot.end.includes("T")
                ? selectedSlot.end.split("T")[1].substring(0, 5)
                : selectedSlot.end.substring(0, 5);
            const displayEnd = convertISTToTimezone(endStr, dateStr, selectedTimezone);
            return {
                dateStr: `${day}, ${month} ${date}`,
                time: `${displayStart} – ${displayEnd}`,
                mode,
                duration: null,
            };
        }
        const durationMin = calculateDuration(selectedSlot.start, selectedSlot.end, 30);
        return {
            dateStr: `${day}, ${month} ${date}`,
            time: displayStart,
            mode,
            duration: t("minutes", undefined, { count: durationMin }),
        };
    }, [
        selectedSlot,
        selectedDate,
        selectedTimezone,
        consultationMode,
        slotTimeFormat,
        t,
    ]);
    const charge = booked?.charge ?? state.quote;
    const feeDisplay = useMemo(() => selectedDoctor && charge ? chargeRow(charge, t) : null, [selectedDoctor, charge, t]);
    return (_jsx(Box, { sx: {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: 3,
        }, children: _jsxs(Box, { sx: { textAlign: "center", maxWidth: 420, width: "100%", mx: "auto" }, children: [_jsx(Box, { sx: {
                        mb: 2.5,
                        display: "flex",
                        justifyContent: "center",
                        color: "primary.main",
                    }, children: _jsxs("svg", { width: "104", height: "104", viewBox: "0 0 80 80", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("rect", { width: "80", height: "80", rx: "40", fill: "currentColor", fillOpacity: "0.08" }), _jsx("path", { d: "M22 34H58M22 34V54C22 56.2091 23.7909 58 26 58H54C56.2091 58 58 56.2091 58 54V34M22 34V28C22 25.7909 23.7909 24 26 24H30M58 34V28C58 25.7909 56.2091 24 54 24H50M30 24V20M30 24H50M50 24V20", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("circle", { cx: "56", cy: "56", r: "12", fill: "currentColor" }), _jsx("path", { d: "M51 56L54.5 59.5L61 53", stroke: "#fff", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }), _jsx(Typography, { variant: "h5", sx: { fontWeight: 700, mb: 0.75 }, children: t("appointmentConfirmedTitle") }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 3.5, lineHeight: 1.5 }, children: t("successMessage") }), _jsxs(Card, { variant: "outlined", sx: {
                        textAlign: "left",
                        position: "relative",
                        borderRadius: 2.5,
                        mt: 1.5,
                        overflow: "visible",
                    }, children: [_jsx(Typography, { variant: "overline", sx: {
                                position: "absolute",
                                top: -9,
                                left: 16,
                                bgcolor: "background.default",
                                px: 1,
                                fontWeight: 700,
                                letterSpacing: 0.8,
                                color: "text.secondary",
                                lineHeight: "18px",
                            }, children: t("bookingSummary") }), _jsxs(CardContent, { sx: { pt: 2.25, pb: "14px !important", px: 2.5 }, children: [patientName && _jsx(Row, { label: t("patient"), value: patientName }), _jsx(Row, { label: t("phone"), value: `${countryCode} ${patientPhone}` }), selectedDoctorDisplay?.name && (_jsx(Row, { label: t("doctor"), value: selectedDoctorDisplay.name, accent: true })), timeDisplay && (_jsxs(_Fragment, { children: [_jsx(Row, { label: t("date"), value: timeDisplay.dateStr }), _jsx(Row, { label: t("time"), value: timeDisplay.time }), _jsx(Row, { label: t("mode"), value: timeDisplay.mode, badge: true }), showDuration && timeDisplay.duration && (_jsx(Row, { label: t("duration"), value: timeDisplay.duration }))] })), feeDisplay && (_jsxs(_Fragment, { children: [_jsx(Row, { label: feeDisplay.label, value: feeDisplay.value, bold: true }), feeDisplay.caption && (_jsx(Row, { label: "", value: feeDisplay.caption, muted: true }))] }))] })] })] }) }));
}
function Row({ label, value, accent, badge, bold, muted, }) {
    return (_jsxs(Box, { sx: {
            display: "grid",
            gridTemplateColumns: "minmax(92px, 40%) 1fr",
            alignItems: "start",
            columnGap: 1.25,
            py: muted ? 0.25 : 0.625,
        }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), badge ? (_jsx(Chip, { label: value, size: "small", variant: "outlined", sx: { fontWeight: 600, fontSize: 12, justifySelf: "end" } })) : (_jsx(Typography, { variant: muted ? "caption" : "body2", sx: {
                    fontWeight: muted ? 500 : bold ? 700 : 600,
                    color: muted
                        ? "success.main"
                        : accent
                            ? "primary.main"
                            : bold
                                ? "primary.main"
                                : "text.primary",
                    textAlign: "right",
                    overflowWrap: "anywhere",
                }, children: value }))] }));
}
